/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useState, useEffect, ChangeEvent } from 'react';
import { Button, Checkbox, FormControlLabel, Paper, InputLabel, List, OutlinedInput } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import moment from 'moment';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';

import { DateTimeRange } from '../../../shared/components/DateTime/DateTimeRangePicker';
import CustomAndRelativeDateTimeRangePicker from '../../../shared/components/DateTime/CustomAndRelativeDateTimeRangePicker';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import SnackConfig from '../../../shared/config/snackConfig';
import { EnqueueJobRequest, Job, submitJob } from '../../../shared/services/job';
import { mainStyles } from '../../../shared/styles/common';
import { round } from '../../../shared/utils/math';
import Hover from './Hover';
import OnlineMetricsJob, { MetricsListData, MetricsTableRowData } from './OnlineMetricsJob';
import OnlineMetricsJobResults from './OnlineMetricsJobResults';
import TimeseriesExpression from './TimeseriesExpression';

const useStyles = makeStyles((theme: Theme) => ({
  overview: {
    padding: '10px',
    marginBottom: '10px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  buttonGroup: {
    margin: theme.spacing(1),
    width: '25%',
    display: 'grid',
    gridTemplateAreas: `
      'button1 button2'
`,
    gridTemplateColumns: 'auto auto',
    columnGap: theme.spacing(1),
  },
  button1: {
    gridArea: 'button1',
  },
  button2: {
    gridArea: 'button2',
  },
  title: {
    textAlign: 'center',
  },
  spacing: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

interface SimilarityRankingIssue {
  name: string;
  labels: Record<string, string>;
}

interface SimilarityRankingCandidate {
  name: string;
  labels: Record<string, string>;
  distance?: number;
  error?: string;
}

export interface SimilarityRankingJobResult {
  error?: string;
  result?: {
    issue: SimilarityRankingIssue;
    candidates: SimilarityRankingCandidate[];
  };
}

export interface SimilarityRankWithClusteringJobResult {
  error?: string;
  result?: {
    issue: SimilarityRankingIssue;
    candidates: Record<string, SimilarityRankingCandidate[]>;
  };
}

const getMinMax = (candidates: SimilarityRankingCandidate[]) => {
  if (candidates.length === 0) {
    return {
      min: undefined,
      max: undefined,
    };
  }
  const min = !candidates[0].error ? candidates[0].distance : undefined;
  let max: number | undefined;
  for (let i = candidates.length - 1; i >= 0; i -= 1) {
    if (!candidates[i].error) {
      max = candidates[i].distance;
      break;
    }
  }
  return { min, max };
};

const getPerc = (value: number, min: number | undefined, max: number | undefined): string | undefined => {
  if (typeof min === 'undefined' || typeof max === 'undefined') {
    return undefined;
  }
  if (min === max) {
    return '100.00%';
  }
  return `${round(((max - value) / (max - min)) * 100)}%`;
};

const getMetricTableData = (
  candidates: SimilarityRankingCandidate[],
  primaryName: string,
  primaryMetricSelectors: string
): MetricsTableRowData[] => {
  const { min, max } = getMinMax(candidates);
  return candidates.map(({ name, labels, distance, error }) => ({
    primaryName,
    primaryMetricSelectors,
    secondaryName: name.trim(),
    secondaryMetricSelectors: JSON.stringify(labels),
    value: error ? 'FAILED' : getPerc(distance!, min, max) || 'N/A',
    hover: !error ? distance : undefined,
  }));
};

export const transformResults = (job: Job): MetricsListData => {
  const { type, results } = job;
  const jobResults = results as SimilarityRankingJobResult;
  const listData: MetricsListData = {};

  if (jobResults && jobResults.result) {
    const { issue, candidates } = jobResults.result;
    const primaryName = issue.name.trim();
    const primaryMetricSelectors = JSON.stringify(issue.labels);

    if (type === 'SIMILARITY_RANKING') {
      listData['0'] = getMetricTableData(candidates, primaryName, primaryMetricSelectors);
    } else if (type === 'SIMILARITY_RANK_WITH_CLUSTERING') {
      const { candidates: candidatesMap } = (results as SimilarityRankWithClusteringJobResult).result!;
      Object.entries(candidatesMap).forEach(([clusterId, cluster]) => {
        listData[clusterId] = getMetricTableData(cluster, primaryName, primaryMetricSelectors);
      });
    }
  }

  return listData;
};

export const columns = [
  {
    title: 'Timeseries',
    type: 'string',
    cellStyle: {
      width: '100%',
    },
    // eslint-disable-next-line react/display-name
    render: ({ secondaryName, secondaryMetricSelectors }: MetricsTableRowData): React.ReactNode => (
      <TimeseriesExpression name={secondaryName!} labels={secondaryMetricSelectors!} />
    ),
  },
  {
    title: 'Score',
    type: 'numeric',
    // eslint-disable-next-line react/display-name
    render: ({ value, hover }: MetricsTableRowData): React.ReactNode => <Hover value={value} hover={hover} />,
  },
];

const JOB_TYPE_NORMAL = 'SIMILARITY_RANKING';
const JOB_TYPE_CLUSTERED = 'SIMILARITY_RANK_WITH_CLUSTERING';
const JOB_TYPES = [JOB_TYPE_NORMAL, JOB_TYPE_CLUSTERED];

type SimilarityRankingProps = RouteComponentProps;

const SimilarityRanking: React.FC<SimilarityRankingProps> = ({ location }: SimilarityRankingProps) => {
  const classes = { ...mainStyles(), ...useStyles() };

  const { issue } = queryString.parse(location.search) as Record<string, string>;

  const [primarySelectors, setPrimarySelectors] = useState<string>(issue);
  const [secondarySelectors, setSecondarySelectors] = useState<string[]>([]);
  const [clustered, setClustered] = useState(true);
  const [timeRange, setTimeRange] = useState<DateTimeRange>({
    start: moment().startOf('minute').subtract(1, 'h'),
    end: moment().startOf('minute'),
  });

  const [job, setJob] = useState<Job>();
  const [jobHistoryJobId, setJobHistoryJobId] = useState<number>();
  const [running, setRunning] = useState<boolean>(false);
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [showJobPanel, setShowJobPanel] = useState(false);

  const [snackConfig, setSnackConfig] = useState<SnackConfig>(new SnackConfig({}));

  const handlePrimarySelectorsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPrimarySelectors(event.target.value);
  };

  const handleSecondarySelectorsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSecondarySelectors(event.target.value.split('|'));
  };

  const runJob = async () => {
    setRunning(true);
    try {
      const request: EnqueueJobRequest = {
        type: clustered ? JOB_TYPE_CLUSTERED : JOB_TYPE_NORMAL,
        options: {
          startTime: timeRange.start.toISOString(),
          endTime: timeRange.end.toISOString(),
          primarySelectors,
          secondarySelectors,
        },
      };
      const data = await submitJob(request);
      setJob(data!.enqueueJob);
      if (!showJobHistory) {
        setShowJobPanel(true);
        setJobHistoryJobId(undefined);
      } else {
        setJobHistoryJobId(data!.enqueueJob.id);
      }
    } catch (error) {
      console.error(`[runJob] error: ${JSON.stringify(error)}`);
      setSnackConfig(new SnackConfig({ open: true, message: JSON.stringify(error) }));
    }
    setRunning(false);
  };

  useEffect(() => {
    if (showJobHistory) {
      setShowJobPanel(false);
    } else {
      setJobHistoryJobId(undefined);
    }
  }, [showJobHistory]);

  const showHistory = () => {
    setShowJobHistory((prev) => !prev);
  };

  return (
    <div className={classes.container}>
      <Paper className={classes.overview}>
        <h2 className={classes.title}>Recommendation</h2>
        <SpinnerOverlay active={running}>
          <div>
            <CustomAndRelativeDateTimeRangePicker
              updateTimeRange={setTimeRange}
              initialTimeRange={timeRange}
              rangePickerName="Set Time Range"
            />
          </div>
          <div className={classes.spacing}>
            <InputLabel htmlFor="primary-selectors">Issue timeseries</InputLabel>
            <OutlinedInput
              value={primarySelectors}
              onChange={handlePrimarySelectorsChange}
              inputProps={{
                name: 'primarySelectors',
                id: 'primary-selectors-query',
              }}
              fullWidth
              labelWidth={60}
            />
          </div>
          <div className={classes.spacing}>
            <InputLabel htmlFor="secondary-selectors">Candidate space</InputLabel>
            <OutlinedInput
              value={secondarySelectors.join('|')}
              onChange={handleSecondarySelectorsChange}
              inputProps={{
                name: 'secondarySelectors',
                id: 'secondary-selectors-query',
              }}
              fullWidth
              labelWidth={60}
            />
          </div>
          <div className={classes.spacing}>
            <FormControlLabel
              control={
                <Checkbox checked={clustered} onChange={(e) => setClustered(e.target.checked)} value={clustered} />
              }
              label="Clustered"
            />
          </div>
          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={runJob}
              disabled={
                secondarySelectors.length === 0 ||
                typeof primarySelectors === 'undefined' ||
                primarySelectors.length === 0 ||
                running
              }
              className={classes.button1}
            >
              Start Ranking
            </Button>
            <Button
              variant={showJobHistory ? 'contained' : 'outlined'}
              size="small"
              color="primary"
              onClick={showHistory}
              className={classes.button2}
            >
              Ranking History
            </Button>
          </div>
        </SpinnerOverlay>
      </Paper>
      {showJobPanel && job && (
        <Paper className={classes.overview}>
          <List>
            <OnlineMetricsJob job={job} transformResults={transformResults} columns={columns} enableCloning={false} />
          </List>
        </Paper>
      )}
      {showJobHistory && (
        <Paper className={classes.overview}>
          <OnlineMetricsJobResults
            jobId={jobHistoryJobId}
            jobTypes={JOB_TYPES}
            transformResults={transformResults}
            columns={columns}
          />
        </Paper>
      )}
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default withRouter(SimilarityRanking);
